import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './BoostCreditFacilitiesList.scss'
import { boostCreditBXService } from 'BoostXpressAdmin/Services/boostCreditBXService'
import { BoostCreditFacility } from 'BoostXpressAdmin/Model/boostCreditXModel'
import { CurrencyFormatter, TimeConverter } from 'Helpers'
import { HorizontalLoader } from 'Components'

interface BoostCreditFacilitiesListProps {
  userResourceId: string
}

const BoostCreditFacilitiesList: React.FC<BoostCreditFacilitiesListProps> = ({ userResourceId }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [facilities, setFacilities] = useState<BoostCreditFacility[]>([])
  const [statusFilter, setStatusFilter] = useState('')

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

  const loadFacilities = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: any = {
        page: 1,
        limit: 50,
        sortOrder: 'DESC',
        userResourceId,
      }

      if (statusFilter) params.status = statusFilter

      const res = await boostCreditBXService.getUserFacilities(params)
      setFacilities(res.data?.data || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load facilities')
      setFacilities([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFacilities()
  }, [statusFilter, userResourceId])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.bx-action-dropdown-wrapper')) {
        setOpenDropdownId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981'
      case 'pending_activation':
        return '#f59e0b'
      case 'paused':
        return '#3b82f6'
      case 'suspended':
        return '#ef4444'
      case 'blacklisted':
        return '#dc2626'
      default:
        return '#6b7280'
    }
  }

  const getStatusBgColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#d1fae5'
      case 'pending_activation':
        return '#fef3c7'
      case 'paused':
        return '#dbeafe'
      case 'suspended':
        return '#fee2e2'
      case 'blacklisted':
        return '#fecaca'
      default:
        return '#f3f4f6'
    }
  }

  return (
    <section className='bx-details-section'>
      <h4 className='bx-details-section-title'>Credit Facilities</h4>
      <div className='bx-transactions-history-box'>
        <div className='bx-history-toolbar-wrap row g-2 align-items-center'>
          <div className='col-12 col-md-4'>
            <select
              className='form-control'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value=''>All Statuses</option>
              <option value='pending_activation'>Pending Activation</option>
              <option value='active'>Active</option>
              <option value='paused'>Paused</option>
              <option value='suspended'>Suspended</option>
              <option value='blacklisted'>Blacklisted</option>
            </select>
          </div>
        </div>
        <div className='bx-history-table-wrapper'>
          <table className='bx-history-table'>
            <thead>
              <tr>
                <th>S/N</th>
                <th>Facility ID</th>
                <th>Status</th>
                <th>Gross Limit</th>
                <th>Outstanding</th>
                <th>Available</th>
                <th>Mandate ID</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className='text-center'>
                    <HorizontalLoader />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className='text-center'>
                    <div className='alert alert-danger'>{error}</div>
                  </td>
                </tr>
              ) : facilities.length === 0 ? (
                <tr>
                  <td colSpan={9} className='text-center'>
                    No facilities found
                  </td>
                </tr>
              ) : (
                facilities.map((f: BoostCreditFacility, index) => (
                  <tr key={f.resourceId}>
                    <td>{index + 1}</td>
                    <td className='bx-id-cell'>{f.resourceId}</td>
                    <td>
                      <span
                        className='bx-status-badge'
                        style={{
                          backgroundColor: getStatusBgColor(f.status),
                          color: getStatusBadgeColor(f.status),
                        }}
                      >
                        {f.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(f.grossLimitMajor)}</td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(f.outstandingMajor)}</td>
                    <td className='bx-amount-cell'>₦{CurrencyFormatter(f.availableMajor)}</td>
                    <td>{f.mandateId || '-'}</td>
                    <td>{TimeConverter(f.createdAt)}</td>
                    <td>
                      <div className='bx-action-dropdown-wrapper'>
                        <button
                          className='bx-action-dropdown-toggle'
                          onClick={() => setOpenDropdownId(openDropdownId === f.resourceId ? null : f.resourceId)}
                        >
                          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <circle cx='12' cy='12' r='2' fill='#6b7280'/>
                            <circle cx='12' cy='6' r='2' fill='#6b7280'/>
                            <circle cx='12' cy='18' r='2' fill='#6b7280'/>
                          </svg>
                        </button>
                        {openDropdownId === f.resourceId && (
                          <div className={`bx-action-dropdown-menu ${facilities.length === 1 ? 'bx-dropdown-up' : ''}`}>
                            <div
                              className='bx-action-dropdown-item'
                              onClick={() => {
                                setOpenDropdownId(null)
                                navigate(`/boostxpress/boost-credit/facility/${f.resourceId}`, {
                                  state: { facility: f },
                                })
                              }}
                            >
                              View Details
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

export default BoostCreditFacilitiesList
